import { call, put, takeLatest } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import { sendAction, type SuperClientConfig } from '../api';
import {
  fetchActiveEotSuccess,
  fetchActiveEotError,
  archiveActiveEotSuccess,
  archiveActiveEotError,
} from '../actions';
import { SUPER_ADMIN_FETCH_ACTIVE_EOT, SUPER_ADMIN_ARCHIVE_ACTIVE_EOT } from '../types';

interface FetchActiveEotAction {
  type: string;
  userId: string;
  productAccessId: string;
}

interface ArchiveActiveEotAction {
  type: string;
  eotId: string;
}

interface EotRow {
  id: string;
  start_date: string;
  end_date: string;
  archive_date: string | null;
}

interface SendActionResponse {
  succeeded?: boolean;
  data?: { rows?: EotRow[] };
}

export function* fetchActiveEotSaga(
  config: SuperClientConfig,
  action: FetchActiveEotAction,
): Generator<unknown, void, unknown> {
  try {
    const { userId, productAccessId } = action;
    const date = new Date().toISOString();

    const getAction = CloudActions.getExtendedOfflineTokenForUserAtDate(
      userId,
      date,
      productAccessId,
    );
    const res = (yield call(sendAction, config, getAction)) as SendActionResponse;

    if (!res.succeeded) {
      yield put(fetchActiveEotError('Failed to fetch active EOT.'));
      return;
    }

    const rows = res.data?.rows ?? [];
    const activeRow = rows.find((row) => row.archive_date == null);

    if (!activeRow) {
      yield put(fetchActiveEotSuccess(null));
      return;
    }

    yield put(
      fetchActiveEotSuccess({
        id: activeRow.id,
        startDate: activeRow.start_date,
        endDate: activeRow.end_date,
      }),
    );
  } catch (error) {
    console.error('fetchActiveEot failed -- ', error);
    yield put(fetchActiveEotError('An unexpected error occurred while fetching EOT.'));
  }
}

export function* archiveActiveEotSaga(
  config: SuperClientConfig,
  action: ArchiveActiveEotAction,
): Generator<unknown, void, unknown> {
  try {
    const { eotId } = action;
    const archiveAction = CloudActions.archiveExtendedOfflineTokenById(eotId);
    const res = (yield call(sendAction, config, archiveAction)) as SendActionResponse;

    if (!res.succeeded) {
      yield put(archiveActiveEotError('Archiving the EOT failed.'));
      return;
    }

    yield put(archiveActiveEotSuccess());
  } catch (error) {
    console.error('archiveActiveEot failed -- ', error);
    yield put(archiveActiveEotError('An unexpected error occurred.'));
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchFetchActiveEot(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_FETCH_ACTIVE_EOT, fetchActiveEotSaga, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchArchiveActiveEot(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_ARCHIVE_ACTIVE_EOT, archiveActiveEotSaga, config);
}
